// ====== AllergoAway Type Definitions ======

export interface DailyLog {
  id?: number;
  date: string; // yyyy-MM-dd
  createdAt: string;
  updatedAt: string;

  // Step 1: Overall VAS (0-10)
  vasScore: number;

  // Step 2: Symptom scores (0-3 each)
  // Nasal (TNSS)
  sneezing: number;
  runnyNose: number;
  congestion: number;
  nasalItch: number;
  // Ocular (TOSS)
  itchyEyes: number;
  wateryEyes: number;
  redEyes: number;
  // Respiratory
  cough: number;
  wheezing: number;
  shortnessOfBreath: number;
  // Skin
  urticaria: number;
  itchingEczema: number;
  // Systemic
  fatigue: number;
  headache: number;

  // ARIA severity (binary)
  affectedSleep: boolean;
  impairedActivities: boolean;
  impairedWork: boolean;
  troublesome: boolean;

  // Step 3: Context - Tier 1
  medicationsTaken: string[];
  sleepHours: number | null;
  outdoorHours: number | null;

  // Tier 2
  sleepQuality: number | null; // 0-10
  stressLevel: number | null; // 0-10
  acOn: boolean;
  acMode: string; // 'recirculation' | 'fresh' | ''
  windowsOpenSea: boolean;
  windowsSeaDuration: number | null; // minutes
  windowsOpenStreet: boolean;
  windowsStreetDuration: number | null; // minutes
  carTravel: boolean;
  carAirMode: string; // 'recirculation' | 'fresh' | ''
  carMinutes: number | null;
  outdoorEnvironment: string[]; // park, urban, countryside, seaside
  exerciseOutdoor: boolean;
  alcoholType: string; // none, red_wine, white_wine, beer, spirits
  alcoholQuantity: number | null;

  // Tier 3
  petContact: boolean;
  petDuration: number | null;
  laundryOutdoor: boolean;
  cleaningDone: boolean;
  cleanedLitterBox: boolean;
  scratchedByMisu: boolean;
  crossReactiveFoods: string[];
  smokingExposure: boolean;
  handledCardboard: boolean;

  notes: string;

  // Tier 4: Auto-populated
  temperature: number | null;
  humidity: number | null;
  windSpeed: number | null;
  rain: number | null;
  pressure: number | null;
  pm25: number | null;
  uvIndex: number | null;
  pollenGrass: number | null;
  pollenBirch: number | null;
  pollenOlive: number | null;
  pollenMugwort: number | null;
  pollenRagweed: number | null;
  pollenAlder: number | null;

  // Entry metadata
  entryTime: 'morning' | 'evening' | 'single';
}

export interface WeeklyAssessment {
  id?: number;
  weekStartDate: string;
  createdAt: string;

  // RCAT (6 items, each 1-5, total 6-30)
  rcatScores: number[];
  rcatTotal: number;

}

export interface UserProfile {
  id?: number;
  name: string;
  region: string; // Italian region
  knownAllergies: string[];
  medications: string[];
  latitude: number | null;
  longitude: number | null;

  // Google Drive sync
  googleDriveSyncEnabled: boolean;
  lastSyncTime: string | null;
  syncIntervalMinutes: number;
}

export interface Medication {
  id?: number;
  name: string;
  type: 'antihistamine' | 'nasal_spray' | 'eye_drops' | 'inhaler' | 'immunotherapy' | 'other';
  timing: 'prophylactic' | 'reactive';
}

export const ITALIAN_REGIONS = [
  'Abruzzo', 'Basilicata', 'Calabria', 'Campania', 'Emilia-Romagna',
  'Friuli Venezia Giulia', 'Lazio', 'Liguria', 'Lombardia', 'Marche',
  'Molise', 'Piemonte', 'Puglia', 'Sardegna', 'Sicilia', 'Toscana',
  'Trentino-Alto Adige', 'Umbria', "Valle d'Aosta", 'Veneto'
];

export const KNOWN_ALLERGENS = [
  'Parietaria', 'Graminacee', 'Olivo', 'Cipresso', 'Betulla',
  'Ambrosia', 'Artemisia', 'Nocciolo', 'Ontano', 'Pioppo',
  'Acari della polvere', 'Alternaria (muffa)', 'Gatto', 'Cane',
  'Cartone/carta vecchia'
];

export const CROSS_REACTIVE_FOODS = [
  'Pesca', 'Pistacchio', 'Basilico', 'Sedano', 'Prezzemolo',
  'Finocchio', 'Camomilla', 'Fichi', 'Piselli', 'Gelso',
  'Mela', 'Ciliegia', 'Noce', 'Nocciola', 'Kiwi'
];

export const SYMPTOM_LABELS: Record<string, string> = {
  sneezing: 'Starnuti',
  runnyNose: 'Naso che cola',
  congestion: 'Congestione nasale',
  nasalItch: 'Prurito nasale',
  itchyEyes: 'Occhi che prudono',
  wateryEyes: 'Occhi che lacrimano',
  redEyes: 'Occhi rossi',
  cough: 'Tosse',
  wheezing: 'Respiro sibilante',
  shortnessOfBreath: 'Difficoltà respiratorie',
  urticaria: 'Orticaria',
  itchingEczema: 'Prurito/Eczema',
  fatigue: 'Stanchezza',
  headache: 'Mal di testa',
};

export const SYMPTOM_CATEGORIES: Record<string, string[]> = {
  'Nasale': ['sneezing', 'runnyNose', 'congestion', 'nasalItch'],
  'Oculare': ['itchyEyes', 'wateryEyes', 'redEyes'],
  'Respiratorio': ['cough', 'wheezing', 'shortnessOfBreath'],
  'Cutaneo': ['urticaria', 'itchingEczema'],
  'Sistemico': ['fatigue', 'headache'],
};

export const SEVERITY_LABELS: Record<number, string> = {
  0: 'Assente',
  1: 'Lieve',
  2: 'Moderato',
  3: 'Grave',
};

export const SEVERITY_COLORS: Record<number, string> = {
  0: '#4caf50',
  1: '#ffeb3b',
  2: '#ff9800',
  3: '#f44336',
};

// Italian pollen calendar
export const POLLEN_CALENDAR: Record<string, Record<number, string>> = {
  'Cipresso': { 1: 'LOW', 2: 'HIGH', 3: 'VERY_HIGH', 4: 'HIGH', 5: 'LOW', 6: '', 7: '', 8: '', 9: '', 10: '', 11: '', 12: '' },
  'Nocciolo': { 1: 'MEDIUM', 2: 'HIGH', 3: 'LOW', 4: '', 5: '', 6: '', 7: '', 8: '', 9: '', 10: '', 11: '', 12: '' },
  'Betulla': { 1: '', 2: '', 3: 'MEDIUM', 4: 'VERY_HIGH', 5: 'MEDIUM', 6: '', 7: '', 8: '', 9: '', 10: '', 11: '', 12: '' },
  'Parietaria': { 1: '', 2: 'LOW', 3: 'MEDIUM', 4: 'HIGH', 5: 'VERY_HIGH', 6: 'HIGH', 7: 'MEDIUM', 8: 'LOW', 9: 'MEDIUM', 10: 'LOW', 11: '', 12: '' },
  'Graminacee': { 1: '', 2: '', 3: 'LOW', 4: 'MEDIUM', 5: 'VERY_HIGH', 6: 'HIGH', 7: 'MEDIUM', 8: 'LOW', 9: '', 10: '', 11: '', 12: '' },
  'Olivo': { 1: '', 2: '', 3: '', 4: 'MEDIUM', 5: 'VERY_HIGH', 6: 'VERY_HIGH', 7: 'LOW', 8: '', 9: '', 10: '', 11: '', 12: '' },
  'Ambrosia': { 1: '', 2: '', 3: '', 4: '', 5: '', 6: '', 7: 'LOW', 8: 'VERY_HIGH', 9: 'MEDIUM', 10: 'LOW', 11: '', 12: '' },
  'Artemisia': { 1: '', 2: '', 3: '', 4: '', 5: '', 6: '', 7: 'MEDIUM', 8: 'VERY_HIGH', 9: 'MEDIUM', 10: '', 11: '', 12: '' },
  'Alternaria': { 1: '', 2: '', 3: '', 4: '', 5: 'MEDIUM', 6: 'HIGH', 7: 'LOW', 8: 'HIGH', 9: 'HIGH', 10: 'MEDIUM', 11: '', 12: '' },
};
